<template>
  <div
    v-if="shouldShowNotification"
    class="progress-top-notification"
    @click="handleClick"
  >
    <div class="progress-icon">
      <i class="fa fa-cog fa-spin"></i>
    </div>
    <div class="progress-content">
      <div class="progress-title">
        <span>
          {{ currentOperation?.type === 'edit' ? 'Editing' : 'Improving' }}
          Template</span
        >
        <span class="elapsed-time">{{ elapsedTime }}</span>
      </div>
      <div class="progress-bar">
        <div
          class="progress-fill"
          :style="{ width: `${currentOperation?.progress || 0}%` }"
        ></div>
      </div>
      <div class="progress-text">{{ currentOperation?.message }}</div>
      <div class="click-hint">Click to view progress</div>
    </div>
    <button class="close-btn" @click.stop="dismiss">×</button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useTemplateProgressStore } from '@/store/templateProgressStore';
import { useRouter } from 'vue-router';

const progressStore = useTemplateProgressStore();
const router = useRouter();

const hasActiveOperations = computed(() => progressStore.hasActiveOperations);
const isMobile = ref(
  typeof window !== 'undefined' ? window.innerWidth <= 768 : false
);
const currentOperation = computed(() => {
  const operations = progressStore.getAllActiveOperations;
  return operations.length > 0 ? operations[0] : null;
});

function updateIsMobile() {
  isMobile.value = window.innerWidth <= 768;
}

// Elapsed time logic
const elapsedTime = ref('');
let intervalId: ReturnType<typeof setInterval> | null = null;

function formatElapsedTime(startTime: number, endTime: number) {
  const totalSeconds = Math.floor((endTime - startTime) / 1000);
  if (totalSeconds < 0) return '00:00';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
}

watch(
  currentOperation,
  (op) => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }

    if (op) {
      const update = () => {
        const end = op.endTime || Date.now();
        elapsedTime.value = formatElapsedTime(op.startTime, end);
      };

      update();

      if (!op.endTime) {
        intervalId = setInterval(update, 1000);
      }
    } else {
      elapsedTime.value = '';
    }
  },
  { deep: true, immediate: true }
);

if (typeof window !== 'undefined') {
  window.addEventListener('resize', updateIsMobile);
}

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateIsMobile);
  }
});

// Show the notification everywhere when there are active operations
const shouldShowNotification = computed(() => {
  if (isMobile.value && currentOperation.value) {
    return false;
  }
  return hasActiveOperations.value && progressStore.showNotifications;
});

function handleClick() {
  if (!currentOperation.value) {
    console.log('[ProgressNotification] No current operation, skipping click');
    return;
  }

  console.log('[ProgressNotification] Click detected, operation:', {
    templateId: currentOperation.value.templateId,
    versionId: currentOperation.value.versionId
  });

  const route = router.currentRoute.value;
  const currentProjectId = getCurrentProjectId();

  console.log(
    '[ProgressNotification] Current route:',
    route.path,
    'projectID:',
    currentProjectId
  );

  // Case 1: Already on a templates-related route - just dispatch the open event
  if (
    route.path.includes('/templates-view/') ||
    route.path.includes('/templates/')
  ) {
    console.log(
      '[ProgressNotification] Case 1: On templates view, dispatching openTemplateEdit'
    );
    progressStore.requestOpenTemplate(
      currentOperation.value.templateId,
      currentOperation.value.versionId
    );
    return;
  }

  // Case 2: Inside P1 project page (tabs) – switch tab via custom event
  if (route.path.includes('/p1/') && route.params.projectID) {
    console.log('[ProgressNotification] Case 2: Switching to templates tab');
    const detail = {
      templateId: currentOperation.value.templateId,
      versionId: currentOperation.value.versionId
    };
    window.dispatchEvent(
      new CustomEvent('switchToTemplatesTabAndOpen', { detail })
    );
    return;
  }

  // Case 3: On other project routes (catalog, etc) - navigate to templates view
  if (currentProjectId && currentProjectId !== 'default') {
    console.log('[ProgressNotification] Case 3: Navigating to templates view');
    router.push(`/templates/${currentProjectId}`);
    setTimeout(() => {
      if (currentOperation.value) {
        progressStore.requestOpenTemplate(
          currentOperation.value.templateId,
          currentOperation.value.versionId
        );
      }
    }, 450);
    return;
  }

  // Case 4: Fallback - navigate to p1 templates view
  console.log('[ProgressNotification] Case 4: Fallback navigation');
  router.push(`/p1/templates-view/${currentProjectId}`);
  setTimeout(() => {
    if (currentOperation.value) {
      progressStore.requestOpenTemplate(
        currentOperation.value.templateId,
        currentOperation.value.versionId
      );
    }
  }, 450);
}

function dismiss() {
  progressStore.hideNotifications();
}

function getCurrentProjectId(): string {
  const route = router.currentRoute.value;
  return (route.params.projectID as string) || 'default';
}
</script>

<style scoped>
.progress-top-notification {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 280px;
  max-width: 400px;
  z-index: 9998;
  cursor: pointer;
}

.progress-top-notification:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  transform: translateY(-1px);
}

.progress-icon {
  color: #1f90e2;
  font-size: 16px;
  flex-shrink: 0;
}

.progress-content {
  flex: 1;
  min-width: 0;
}

.progress-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}

.elapsed-time {
  font-size: 12px;
  font-weight: 400;
  color: #666;
  background-color: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 4px;
}

.progress-fill {
  height: 100%;
  background: #1f90e2;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.click-hint {
  font-size: 11px;
  color: #3b82f6;
  font-style: italic;
  cursor: pointer;
  text-decoration: underline;
}

.click-hint:hover {
  color: #2563eb;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.close-btn:hover {
  color: #666;
}
</style>
